import { type HeatmapCategoryKey, isHeatmapCategoryKey } from './heatmap-config';

type GameMapHeatmapEvents = {
    event_kill: string;
    event_kills: string;
    event_death: string;
    event_deaths: string;
    event_success: string;
    event_successes: string;
    event_failure: string;
    event_failures: string;
    event_achievement: string;
    event_achievements: string;
    event_general: string;
};

type GameMapResources = { heatmap_events: GameMapHeatmapEvents };

export type HeatmapLabelSelector = (resources: GameMapResources) => string;

type CategoryLabelSelectors = {
    readonly plural: HeatmapLabelSelector;
    readonly singular: HeatmapLabelSelector;
};

/**
 * Known heatmap category keys → i18n selectors.
 * Plural is the sidebar checkbox label; singular is interpolated into clip titles
 * (`{{gameCategoryName}} Clip`). Keep both as real strings — do not derive one from
 * the other. Category labels go through `t()`, so English "strip the trailing s"
 * would break every other locale.
 */
const CATEGORY_LABEL_SELECTORS: Readonly<Record<HeatmapCategoryKey, CategoryLabelSelectors>> = {
    kills: {
        plural: $ => $.heatmap_events.event_kills,
        singular: $ => $.heatmap_events.event_kill,
    },
    deaths: {
        plural: $ => $.heatmap_events.event_deaths,
        singular: $ => $.heatmap_events.event_death,
    },
    successes: {
        plural: $ => $.heatmap_events.event_successes,
        singular: $ => $.heatmap_events.event_success,
    },
    failures: {
        plural: $ => $.heatmap_events.event_failures,
        singular: $ => $.heatmap_events.event_failure,
    },
    achievements: {
        plural: $ => $.heatmap_events.event_achievements,
        singular: $ => $.heatmap_events.event_achievement,
    },
    general: {
        plural: $ => $.heatmap_events.event_general,
        singular: $ => $.heatmap_events.event_general,
    },
};

/**
 * Selector for `game_map.heatmap_events` category labels.
 * Pass the result to `t(...)` from `useT(['game_map'])`, or fall back to the raw key when null.
 */
export function getHeatmapEventCategoryLabelSelector(
    categoryKey: string,
    form: 'plural' | 'singular' = 'plural',
): HeatmapLabelSelector | null {
    if (!isHeatmapCategoryKey(categoryKey)) return null;
    return CATEGORY_LABEL_SELECTORS[categoryKey][form];
}
